import { useMemo, useState } from "react";
import { Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  SPORT_TABS,
  useGetCasinoListQuery,
  useGetMatchesBySportQuery,
} from "../../../store/service/clientBetService";
import { formatOdd, resolveCasinoImage } from "./clientUtils";
import "./Client.scss";

const MAIN_TABS = [
  { id: "casino", label: "Casino" },
  { id: "inplay", label: "In-Play" },
  { id: "sports", label: "Sports" },
];

const SPORT_CODES = {
  Cricket: "CRI",
  Football: "FB",
  Tennis: "TEN",
};

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
  </svg>
);

const MatchCard = ({ match, onOpen }) => {
  const hasDraw = Number(match.drawBack) > 0 || Number(match.drawLay) > 0;
  const showOdds = match.hasOdds && !match.oddsSuspended;

  return (
    <article
      className={`lotus-match-card ${match.inPlay ? "is-live" : ""} ${match.oddsSuspended ? "is-suspended" : ""}`}
      onClick={() => onOpen(match.matchId)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(match.matchId)}
      role="button"
      tabIndex={0}>
      <div className="lotus-match-row">
        <div className="lotus-match-info">
          <div className="lotus-match-title-line">
            <h3>{match.matchName}</h3>
            {match.inPlay ? <span className="lotus-pill lotus-pill--live">LIVE</span> : null}
            <span className="lotus-pill lotus-pill--tv">TV</span>
          </div>
          <p className="lotus-match-meta">
            {moment(match.openDate).format("DD MMM · HH:mm")}
            {match.league ? ` · ${match.league}` : ""}
          </p>
        </div>

        {showOdds ? (
          <div className="lotus-match-odds">
            <div className="lotus-odds-col">
              <span className="lotus-col-label">1</span>
              <span className="lotus-odd-back">{formatOdd(match.team1Back)}</span>
              <span className="lotus-odd-lay">{formatOdd(match.team1Lay)}</span>
            </div>
            {hasDraw ? (
              <div className="lotus-odds-col">
                <span className="lotus-col-label">X</span>
                <span className="lotus-odd-back">{formatOdd(match.drawBack)}</span>
                <span className="lotus-odd-lay">{formatOdd(match.drawLay)}</span>
              </div>
            ) : null}
            <div className="lotus-odds-col">
              <span className="lotus-col-label">2</span>
              <span className="lotus-odd-back">{formatOdd(match.team2Back)}</span>
              <span className="lotus-odd-lay">{formatOdd(match.team2Lay)}</span>
            </div>
          </div>
        ) : (
          <div className={`lotus-odds-status ${match.inPlay ? "live" : "soon"}`}>
            <strong>{match.inPlay ? "SUSPENDED" : "SOON"}</strong>
            <span>{match.inPlay ? "Odds paused" : "Odds soon"}</span>
          </div>
        )}
      </div>
    </article>
  );
};

const ClientHome = () => {
  const nav = useNavigate();
  const [mainTab, setMainTab] = useState("casino");
  const [sportId, setSportId] = useState(4);
  const [filterLive, setFilterLive] = useState(false);
  const [filterPremium, setFilterPremium] = useState(false);
  const [sortBy, setSortBy] = useState("time");
  const [search, setSearch] = useState("");

  const { data, isLoading, isFetching } = useGetMatchesBySportQuery(sportId, {
    pollingInterval: 3000,
    skip: mainTab === "casino",
    refetchOnMountOrArgChange: true,
  });

  const { data: cricketTicker } = useGetMatchesBySportQuery(4, {
    pollingInterval: 15000,
  });

  const { data: casinoData } = useGetCasinoListQuery(undefined, {
    pollingInterval: 30000,
  });

  const matches = useMemo(() => {
    let list = [...(data?.data || [])];

    if (filterLive) {
      list = list.filter((m) => m.inPlay);
    }
    if (filterPremium) {
      list = list.filter((m) => m.bm || m.F || m.GM);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          m.matchName?.toLowerCase().includes(q) ||
          m.league?.toLowerCase().includes(q)
      );
    }

    if (sortBy === "time") {
      list.sort((a, b) => new Date(a.openDate) - new Date(b.openDate));
    } else {
      list.sort((a, b) => String(a.matchName).localeCompare(String(b.matchName)));
    }

    if (mainTab === "inplay") {
      list.sort((a, b) => Number(b.inPlay) - Number(a.inPlay));
    }

    return list;
  }, [data, filterLive, filterPremium, search, sortBy, mainTab]);

  const tickerMatches = cricketTicker?.data || [];
  const casinoGames = casinoData?.data || [];
  const liveCount = matches.filter((m) => m.inPlay).length;

  const openMatch = (id) => nav(`/play/match/${id}`);
  const openCasino = (game) => {
    const id = game?.eventId || game?.tableId || game?.id;
    if (!id) {
      message.info("Table id missing");
      return;
    }
    nav(`/play/casino/${id}`);
  };

  return (
    <div className="lotus-home lotus-home--hall">
      <div className="lotus-main-tabs lotus-main-tabs--hall">
        {MAIN_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={mainTab === t.id ? "active" : ""}
            onClick={() => setMainTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {mainTab === "casino" ? (
        <div className="lotus-casino-section lotus-casino-section--hall">
          <div className="lotus-casino-grid lotus-casino-grid--hall">
            {casinoGames.length === 0 ? (
              <div className="lotus-empty" style={{ gridColumn: "1 / -1" }}>
                Loading games…
              </div>
            ) : (
              casinoGames.map((game, i) => (
                <button
                  key={game?.tableId || game?.eventId || game?.id || i}
                  type="button"
                  className="lotus-casino-card"
                  onClick={() => openCasino(game)}>
                  <img src={resolveCasinoImage(game.name, i)} alt={game.name} loading="lazy" />
                  <span className="lotus-casino-card__label">{game.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="lotus-search-row">
            <span className="lotus-search-icon">
              <IconSearch />
            </span>
            <input
              type="search"
              placeholder="Search matches…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="lotus-ticker-wrap">
            <div className="lotus-ticker">
              {[...tickerMatches, ...tickerMatches].map((m, i) => (
                <button key={`${m.matchId}-${i}`} type="button" onClick={() => openMatch(m.matchId)}>
                  {m.matchName} · {moment(m.openDate).format("DD MMM HH:mm")}
                  {m.inPlay ? " · LIVE" : ""}
                </button>
              ))}
            </div>
          </div>

          <div className="lotus-feed-banner">
            <strong>Cricket live</strong>
            <span>Bet + win/loss ready · Football & Tennis soon</span>
          </div>

          <div className="lotus-sport-icons">
            {SPORT_TABS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`lotus-sport-icon ${sportId === s.id ? "active" : ""} ${
                  s.bettable ? "" : "is-soon"
                }`}
                onClick={() => {
                  setSportId(s.id);
                  if (!s.bettable) {
                    message.info(`${s.name} jaldi — abhi Cricket pe bet lagao`);
                  }
                }}>
                <span className="lotus-sport-emoji">
                  {SPORT_CODES[s.name] || s.name.slice(0, 3).toUpperCase()}
                </span>
                <small>
                  {s.name}
                  {!s.bettable ? " · Soon" : ""}
                </small>
              </button>
            ))}
          </div>

          <div className="lotus-filters">
            <button
              type="button"
              className={filterLive ? "active" : ""}
              onClick={() => setFilterLive(!filterLive)}>
              Live
            </button>
            <button
              type="button"
              className={filterPremium ? "active" : ""}
              onClick={() => setFilterPremium(!filterPremium)}>
              Premium
            </button>
            <label className="lotus-sort">
              Sort
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="time">Time</option>
                <option value="name">Name</option>
              </select>
            </label>
          </div>

          <div className="lotus-count-bar">
            {isFetching
              ? "Refreshing odds…"
              : `${matches.length} matches${liveCount ? ` · ${liveCount} live` : ""}`}
          </div>

          {(isLoading || isFetching) && matches.length === 0 ? (
            <div className="lotus-loading">
              <Spin tip="Loading matches…" />
            </div>
          ) : matches.length === 0 ? (
            <div className="lotus-empty">No matches for this sport right now</div>
          ) : (
            <div className="lotus-match-list">
              {matches.map((match) => (
                <MatchCard key={match.matchId} match={match} onOpen={openMatch} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientHome;
